import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import StoreDetailsHeader from '../../../components/storeDetails/StoreDetailsHeader';
import type { StoreDetailsTab } from '../../../types/features/storeDetails/page';
import { useStoreDetailsHeaderContext } from '../hooks/useStoreDetailsHeaderContext';
import { useStoreSwitcher } from '../hooks/useStoreSwitcher';
import { STORE_DETAILS_TAB_PARAM, STORE_DETAILS_TAB_PARAM_LEGACY } from '../utils/storeDetailsQueryParams';
import { getStoreDetailsTabs } from '../utils/storeDetailsTabs';

type StoreScopedFeaturePageProps = {
    storeId?: string;
    backLabel: string;
    backPath: string;
    activeTab: StoreDetailsTab;
    children: React.ReactNode;
};

const StoreScopedFeaturePage: React.FC<StoreScopedFeaturePageProps> = ({
    storeId,
    backLabel,
    backPath,
    activeTab,
    children,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { store, storeOwner } = useStoreDetailsHeaderContext(storeId);
    const { stores: storesForSwitcher } = useStoreSwitcher();

    return (
        <>
            {storeId && (
                <StoreDetailsHeader
                    store={store}
                    storeOwner={storeOwner}
                    showEdit={false}
                    backLabel={backLabel}
                    onBack={() => navigate(backPath)}
                    enableStoreSwitcher
                    storesForSwitcher={storesForSwitcher}
                    activeStoreId={storeId}
                    onStoreChange={(nextStoreId) => {
                        if (!nextStoreId || nextStoreId === storeId) {
                            return;
                        }

                        navigate({
                            pathname: `/dashboard/store-summary/stores/${nextStoreId}`,
                            search: location.search || '',
                        });
                    }}
                    tabs={getStoreDetailsTabs(Boolean(store?.has_vending_machine || store?.enable_manual_payout_entry))}
                    activeTab={activeTab}
                    onTabChange={(tabKey) => {
                        const params = new URLSearchParams(location.search);
                        const desiredTabParam = tabKey === 'overview' ? null : tabKey;

                        if (desiredTabParam) {
                            params.set(STORE_DETAILS_TAB_PARAM, desiredTabParam);
                        } else {
                            params.delete(STORE_DETAILS_TAB_PARAM);
                        }

                        params.delete(STORE_DETAILS_TAB_PARAM_LEGACY);

                        const nextSearch = params.toString();
                        navigate({
                            pathname: `/dashboard/store-summary/stores/${storeId}`,
                            search: nextSearch ? `?${nextSearch}` : '',
                        });
                    }}
                />
            )}

            {children}
        </>
    );
};

export default React.memo(StoreScopedFeaturePage);
